import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  CloseButton,
  Form,
  InputGroup,
  Spinner,
  Alert,
  ListGroup,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  ExchangeProduct,
  ProductsResponse,
  ShopifyProduct,
  ShopifyVariant,
} from '../utils/types';
import { formatPrice } from '../utils/helpers';

interface ShopifyProductsModalProps {
  show: boolean;
  onHide: () => void;
  onSelectProduct: (product: ExchangeProduct) => void;
  shopDomain: string;
  useTokenlessAccess?: boolean;
  storefrontAccessToken?: string;
  minQuantity?: number;
  maxPrice?: number;
  productTitle?: string;
  variantTitle?: string;
  id?: string;
}

interface GraphQLErrorResponse {
  errors: { message: string }[];
}

type SortOption = 'TITLE' | 'PRICE';

const ShopifyProductsModal: React.FC<ShopifyProductsModalProps> = ({
  show,
  onHide,
  onSelectProduct,
  shopDomain,
  useTokenlessAccess = true,
  storefrontAccessToken = '',
  minQuantity = 1,
  maxPrice,
  productTitle,
  variantTitle,
  id,
}) => {
  const { t } = useTranslation(['translation', 'dynamic']);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('TITLE');
  const [productType, setProductType] = useState('');
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null
  );
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set()
  );
  const [types, setTypes] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // No Modal, so no built-in focus handling: move focus into the panel on
  // open and give it back to the previously focused element (the trigger)
  // when the panel closes or unmounts, unless the user already moved focus
  // elsewhere (e.g. onto another row's Exchange button).
  useEffect(() => {
    if (!show) return;
    const active = document.activeElement;
    const previous =
      active instanceof HTMLElement && active !== document.body ? active : null;
    const panel = panelRef.current;
    searchInputRef.current?.focus();
    return () => {
      const current = document.activeElement;
      const focusStillHere =
        current === null ||
        current === document.body ||
        (panel?.contains(current) ?? false);
      if (focusStillHere && previous?.isConnected) previous.focus();
    };
  }, [show]);

  const STOREFRONT_API_URL = `https://${shopDomain}/api/2025-10/graphql.json`;

  const productsQuery = `
    query GetProducts($first: Int!, $query: String, $sortKey: ProductSortKeys, $after: String) {
      products(first: $first, query: $query, sortKey: $sortKey, after: $after) {
        edges {
          node {
            id
            title
            description
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            productType
            vendor
            availableForSale
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const productTypesQuery = `
  {
    products(first: 250) {
      edges {
        node {
          productType
        }
      }
    }
  }
`;

  const fetchProducts = async (isLoadMore = false) => {
    setLoading(true);
    setError(null);

    try {
      const queryParts: string[] = [
        'available_for_sale:true',
        `inventory_quantity:>=${minQuantity}`,
      ];

      // Add search term to query - searches product title
      if (searchTerm.trim()) {
        queryParts.push(
          `(title:*${searchTerm.trim()}* OR tag:*${searchTerm.trim()}*)`
        );
      }

      // Add product type filter
      if (productType.trim()) {
        queryParts.push(`product_type:*${productType.trim()}*`);
      }

      const queryString = queryParts.join(' AND ');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (!useTokenlessAccess && storefrontAccessToken) {
        headers['X-Shopify-Storefront-Access-Token'] = storefrontAccessToken;
      }

      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: productsQuery,
          variables: {
            first: 10,
            query: queryString,
            sortKey: sortBy,
            after: isLoadMore ? endCursor : null,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ProductsResponse = await response.json();

      if ('errors' in result) {
        if (isGraphQLErrorResponse(result)) {
          throw new Error(result.errors[0]?.message || 'GraphQL error');
        }
      }

      if (result.data?.products) {
        const newProducts = result.data.products.edges
          .map(edge => {
            const product = edge.node;
            let filteredVariants = product.variants.edges;
            if (maxPrice !== undefined && maxPrice !== null) {
              filteredVariants = product.variants.edges.filter(
                v => Number(v.node.price.amount) <= maxPrice
              );
            }
            if (
              productTitle !== undefined &&
              productTitle !== null &&
              variantTitle !== undefined
            ) {
              if (variantTitle === null) {
                variantTitle = 'Default Title';
              }
              filteredVariants = filteredVariants.filter(
                v =>
                  !(
                    product.title === productTitle &&
                    v.node.title === variantTitle
                  )
              );
            }
            return {
              ...product,
              variants: {
                ...product.variants,
                edges: filteredVariants,
              },
            };
          })
          .filter(product => product.variants.edges.length > 0);

        if (isLoadMore) {
          setProducts(prev => [...prev, ...newProducts]);
        } else {
          setProducts(newProducts);
        }

        setHasNextPage(result.data.products.pageInfo.hasNextPage);
        setEndCursor(result.data.products.pageInfo.endCursor);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch products';
      setError(errorMessage);
      console.error('Shopify API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (!useTokenlessAccess && storefrontAccessToken) {
        headers['X-Shopify-Storefront-Access-Token'] = storefrontAccessToken;
      }

      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: productTypesQuery }),
      });

      const result = await response.json();

      const types: string[] = Array.from(
        new Set(
          result.data.products.edges
            .map((edge: unknown) => {
              const e = edge as { node?: { productType?: string } };
              return e.node?.productType ?? '';
            })
            .filter((t: string) => t && t.trim())
        )
      );

      setTypes(types);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  useEffect(() => {
    if (show) {
      fetchProducts();
      fetchCategories();
      setSelectedVariantId(null);
      setExpandedProducts(new Set());
    }
  }, [show, sortBy, minQuantity, maxPrice]);

  useEffect(() => {
    if (!show) return;

    const timeoutId = setTimeout(() => {
      fetchProducts();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, productType]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchProducts();
  };

  const handleLoadMore = () => {
    if (hasNextPage && !loading) {
      fetchProducts(true);
    }
  };

  const toggleProductExpanded = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleVariantSelect = (
    product: ShopifyProduct,
    variant: ShopifyVariant
  ) => {
    if (!variant.availableForSale) return;
    setSelectedVariantId(variant.id);
  };

  const handleConfirmSelection = () => {
    if (!selectedVariantId) return;

    let selectedProduct: ShopifyProduct | null = null;
    let selectedVariant: ShopifyVariant | null = null;

    for (const product of products) {
      const variant = product.variants.edges.find(
        edge => edge.node.id === selectedVariantId
      )?.node;
      if (variant) {
        selectedProduct = product;
        selectedVariant = variant;
        break;
      }
    }

    if (!selectedProduct || !selectedVariant) return;

    const imageUrl =
      selectedVariant.image?.url ||
      selectedProduct.images.edges[0]?.node.url ||
      '';

    const discount = selectedVariant.compareAtPrice
      ? (
          parseFloat(selectedVariant.compareAtPrice.amount) -
          parseFloat(selectedVariant.price.amount)
        ).toFixed(2)
      : '0.00';

    const productData: ExchangeProduct = {
      id: 0,
      product_title: selectedProduct.title,
      variant_title: selectedVariant.title,
      price: Number(selectedVariant.price.amount),
      currency: selectedVariant.price.currencyCode,
      image_url: imageUrl,
      total_discount: Number(discount),
      product_id: selectedProduct.id,
      variant_id: selectedVariant.id,
    };

    onSelectProduct(productData);
    onHide();
  };

  const groupedProducts = products.reduce(
    (acc, product) => {
      if (!acc[product.title]) {
        acc[product.title] = [];
      }
      acc[product.title].push(product);
      return acc;
    },
    {} as Record<string, ShopifyProduct[]>
  );

  function isGraphQLErrorResponse(
    result: unknown
  ): result is GraphQLErrorResponse {
    if (typeof result === 'object' && result !== null && 'errors' in result) {
      const r = result as { errors?: unknown };
      return Array.isArray(r.errors);
    }
    return false;
  }

  // #78: rendered inline, in place inside the product line (no Modal, no
  // portal). The portal iframe is resized to its full content height, so a
  // position:fixed modal lands at the top of the page and never scrolls.
  if (!show) return null;

  return (
    <div
      id={id}
      ref={panelRef}
      className="card xc-exchange-picker mt-2 text-start"
      onKeyDown={e => {
        if (e.key === 'Escape' && !e.defaultPrevented) onHide();
      }}
    >
      <div className="card-header d-flex align-items-center justify-content-between gap-2">
        <h2 className="h6 mb-0">{t('exchangeForm.title')}</h2>
        <CloseButton onClick={onHide} />
      </div>
      <div className="card-body">
        {/* Search and Filters */}
        <Form onSubmit={handleSearch} className="mb-3">
          <InputGroup className="mb-3">
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder={t('exchangeForm.search') ?? 'Search products...'}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Button
              variant="primary"
              type="submit"
              size="sm"
              disabled={loading}
            >
              {t('exchangeForm.searchButton')}
            </Button>
          </InputGroup>

          <div className="row g-2">
            <div className="col-md-6">
              <Form.Select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortOption)}
                disabled={loading}
              >
                <option value="TITLE">{t('exchangeForm.sortTitle')}</option>
                <option value="PRICE">{t('exchangeForm.sortPrice')}</option>
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Select
                value={productType}
                onChange={e => setProductType(e.target.value)}
                disabled={loading}
              >
                <option value="">{t('exchangeForm.filterType')}</option>
                {types.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Form>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <strong>{t('exchangeForm.error')}:</strong> {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && products.length === 0 && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('buttons.loading')}</span>
            </Spinner>
            <p className="mt-2 text-muted">{t('exchangeForm.fetching')}</p>
          </div>
        )}

        {/* No Results */}
        {!loading && products.length === 0 && !error && (
          <div className="text-center py-4 text-muted">
            <p>
              {searchTerm || productType
                ? 'No products match your search'
                : t('exchangeForm.noProducts')}
            </p>
          </div>
        )}

        {/* Products List: own scroll area, px cap (vh = iframe height) */}
        <div
          className="xc-exchange-picker__list"
          style={{
            maxHeight: 420,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <ListGroup>
            {Object.entries(groupedProducts).map(
              ([productTitle, productGroup]) => {
                const mainProduct = productGroup[0];
                const isExpanded = expandedProducts.has(mainProduct.id);
                const hasMultipleVariants =
                  mainProduct.variants.edges.length > 1;

                return (
                  <div key={mainProduct.id}>
                    {/* Product Header */}
                    <ListGroup.Item
                      className="d-flex align-items-center border-0 border-bottom"
                      style={{
                        cursor: hasMultipleVariants ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (hasMultipleVariants) {
                          toggleProductExpanded(mainProduct.id);
                        } else {
                          handleVariantSelect(
                            mainProduct,
                            mainProduct.variants.edges[0].node
                          );
                        }
                      }}
                    >
                      {!hasMultipleVariants && (
                        <Form.Check
                          type="checkbox"
                          className="me-3"
                          checked={
                            selectedVariantId ===
                            mainProduct.variants.edges[0].node.id
                          }
                          onChange={() => {
                            handleVariantSelect(
                              mainProduct,
                              mainProduct.variants.edges[0].node
                            );
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                      {mainProduct.images.edges[0] && (
                        <img
                          src={mainProduct.images.edges[0].node.url}
                          alt={
                            mainProduct.images.edges[0].node.altText ||
                            mainProduct.title
                          }
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            marginRight: '15px',
                            borderRadius: '4px',
                            marginLeft: hasMultipleVariants ? '40px' : '0',
                          }}
                        />
                      )}
                      <div className="flex-grow-1">
                        <strong>{productTitle}</strong>
                      </div>
                      {!hasMultipleVariants && (
                        <div className="text-end me-2">
                          <div
                            className="text-muted"
                            style={{ fontSize: '0.85rem' }}
                          >
                            {formatPrice(
                              Number(
                                mainProduct.variants.edges[0].node.price.amount
                              ),
                              mainProduct.variants.edges[0].node.price
                                .currencyCode
                            )}
                          </div>
                        </div>
                      )}
                      {hasMultipleVariants && (
                        <span
                          className="text-muted"
                          style={{ fontSize: '1rem' }}
                        >
                          <i
                            className={`bi ${isExpanded ? 'bi-caret-down-fill' : 'bi-caret-right-fill'}`}
                          ></i>
                        </span>
                      )}
                    </ListGroup.Item>

                    {hasMultipleVariants &&
                      isExpanded &&
                      mainProduct.variants.edges.map(({ node: variant }) => (
                        <ListGroup.Item
                          key={variant.id}
                          className="d-flex align-items-center justify-content-between border-0 border-bottom ps-5"
                          style={{
                            cursor: variant.availableForSale
                              ? 'pointer'
                              : 'not-allowed',
                            opacity: variant.availableForSale ? 1 : 0.6,
                          }}
                          onClick={() =>
                            handleVariantSelect(mainProduct, variant)
                          }
                        >
                          <div className="d-flex align-items-center flex-grow-1">
                            <input
                              type="checkbox"
                              className="form-check-input me-3 m-0"
                              checked={selectedVariantId === variant.id}
                              onChange={() =>
                                handleVariantSelect(mainProduct, variant)
                              }
                              disabled={!variant.availableForSale}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className="mb-0"> {variant.title}</span>
                          </div>
                          <div className="text-end">
                            <div
                              className="text-muted"
                              style={{ fontSize: '0.85rem' }}
                            >
                              {formatPrice(
                                Number(variant.price.amount),
                                variant.price.currencyCode
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                  </div>
                );
              }
            )}
          </ListGroup>

          {/* Load More */}
          {hasNextPage && (
            <div className="text-center mt-3">
              <Button
                variant="outline-primary"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('buttons.loading')}
                  </>
                ) : (
                  t('exchangeForm.loadMore')
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="card-footer d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          {t('buttons.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmSelection}
          size="sm"
          disabled={!selectedVariantId}
        >
          {t('exchangeForm.save')}
        </Button>
      </div>
    </div>
  );
};

export default ShopifyProductsModal;
